<template>
    <div class="container">
        <div class="bottom-border-solid" v-for="groupOffers in chunkOffers">
            <div class="row">
                <div :class="col_md" class="pt-5" v-for="offer in groupOffers">
                    <div class="offer-header">
                        <div class="offer-image d-flex justify-content-center">
                            <div class="d-flex justify-content-center">
                                <img :alt="offer.lender?.name" :src="offer.lender?.logo?.url" />
                            </div>
                        </div>
                        <h4 class="offer-heading" v-html="offer.lender?.name"></h4>
                    </div>
                </div>
            </div>

            <div class="row optout-mobile pt-2 pb-3">
                <div :class="col_md" v-for="offer in groupOffers">
                    <div class="optout" v-html="optout"></div>
                </div>
            </div>

            <div class="row">
                <div :class="col_md" v-for="offer in groupOffers">
                    <div class="offer-content">
                        <!-- Tagline -->
                        <h3 class="offer-tagline" v-html="offer.loan_offer?.loan?.rapr"></h3>
                    </div>
                </div>
            </div>

            <div class="row">
                <div :class="col_md" v-for="offer in groupOffers">
                    <div class="offer-content">
                        <!-- Key Benefits - Bulleted Format -->
                        <div>
                            <p><b>Key benefits</b></p>
                            <ol class="offer-ol" style="list-style-type: disc">
                                <li v-for="feature in offer.loan_offer?.loan?.features?.feature">
                                    <p v-html="feature"></p>
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row">
                <div :class="col_md" v-for="offer in groupOffers">
                    <div class="offer-content">
                        <!-- Annual Fee -->
                        <div>
                            <b>Annual fee</b>
                            <p>
                                <u>
                                    <a :href="offer.lender.terms_url?.terms_hyperlink" :target="annualLinkTarget">
                                        {{ formatCurrency(offer.fees ?? 0, true) }}
                                    </a>
                                </u>
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row">
                <div :class="col_md" v-for="offer in groupOffers">
                    <div class="offer-content">
                        <!-- Additional Information -->
                        <div class="additional-info">
                            <b>Additional information</b>
                            <p v-for="info in offer.other_information">
                                {{ info }}
                            </p>
                        </div>
                        <div class="balance-transfer"><p>Balance transfer is available</p></div>
                    </div>
                </div>
            </div>

            <div class="row">
                <div :class="col_md" v-for="offer in groupOffers">
                    <div class="decision d-flex justify-content-center">Get a decision in as little as 60 seconds</div>
                    <div class="offer-footer d-flex justify-content-center mt-2">
                        <button type="button" class="btn btn-primary submitButton" @click="goToOffer(offer)">
                            Accept Now
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { formatCurrency } from '@js/utilities/number.js'
import { useRoute, useRouter } from 'vue-router'
import Bugsnag from '@bugsnag/js'
import { tippy } from 'vue-tippy'

import forEach from 'lodash/forEach'
import split from 'lodash/split'
import isNumber from 'lodash/isNumber'
import sortBy from 'lodash/sortBy'
import isEmpty from 'lodash/isEmpty'
import chunk from 'lodash/chunk'

export default {
    props: ['offers', 'pqdisclosure', 'optout', 'offerOrder', 'annualLinkTarget', 'ctaLinkTarget'],
    data() {
        return {
            offersPerRow: 3,
            windowWidth: window.innerWidth,
        }
    },
    setup() {
        const router = useRouter()
        const route = useRoute()

        return {
            router,
            route,
        }
    },
    mounted() {
        window.addEventListener('resize', () => {
            this.windowWidth = window.innerWidth
            this.offersPerRowResize()
        })

        const observer = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                if (mutation.type === 'childList' && mutation.target === this.$el) {
                    this.addTooltip()
                }
            })
        })

        observer.observe(this.$el, {
            childList: true,
            subtree: true,
        })

        this.offersPerRowResize()
    },
    computed: {
        orderByLender: function () {
            if (this.offers?.length) {
                if (this.offerOrder) {
                    let lenderIdOrder = []

                    forEach(this.offerOrder, order => {
                        forEach(this.offers, offer => {
                            if (
                                offer.lender?.id === order.id ||
                                (!offer.lender?.id && offer.lender?.name === order.name)
                            ) {
                                lenderIdOrder.push(offer)
                            }
                        })
                    })

                    if (!isEmpty(lenderIdOrder)) {
                        return lenderIdOrder
                    }
                }

                return sortBy(this.offers, [
                    function (offer) {
                        return offer.lender?.id
                    },
                ])
            }

            return this.offers
        },
        chunkOffers: function () {
            return chunk(this.orderByLender, this.offersPerRow)
        },
        col_md: that => (that.offersPerRow < 3 ? 'col-md-6' : 'col-md-4'),
    },
    watch: {
        offers: function () {
            this.offersPerRowResize()
        },
    },
    methods: {
        formatCurrency: formatCurrency,
        goToOffer(offer) {
            this.trackOfferClick(offer)
            window.open(offer?.loan_offer?.redirect_url, this.ctaLinkTarget)
        },
        trackOfferClick: function (offer) {
            try {
                this.trackEvent('pageview', {
                    virtualPageView: `${this.route.fullPath}/offer/${offer.id}`,
                    route: `${this.route.fullPath}/offer/${offer.id}`,
                })

                this.trackEvent('event', {
                    eventCategory: 'offer',
                    eventAction: 'click',
                    eventLabel: offer.type,
                    eventValue: offer.monthly_repayment || offer.fees,
                })
            } catch (err) {
                Bugsnag.notify(err)
            }
        },
        offersPerRowResize: function () {
            let defaultSize = 3

            if (this.offers?.length % 3 !== 0 && this.offers?.length < 5) {
                defaultSize = this.offers?.length
            }

            this.offersPerRow = this.windowWidth > '768' ? defaultSize : 1
        },
        addTooltip: function () {
            let pqDisclosureSplit = split(this.pqdisclosure, '<p>')

            let pqDisclosureArray = []
            forEach(pqDisclosureSplit, line => {
                if (isNumber(parseInt(line?.charAt(0))) && !isNaN(parseInt(line?.charAt(0)))) {
                    pqDisclosureArray[parseInt(line?.charAt(0))] = '<p class="text-light">' + line
                }
            })

            forEach(this.$el.querySelectorAll('sup'), sup => {
                sup.classList.add('pointer')
                tippy(sup, {
                    content: pqDisclosureArray[parseInt(sup.innerHTML)],
                    allowHTML: true,
                    arrow: true,
                    animation: 'scale',
                })
            })
        },
    },
}
</script>

<style lang="scss" scoped>
@import '~scss/mixins/rem';
@import '~scss/settings/responsive';

.container {
    display: flex;
    flex-flow: column;
    height: 100%;

    .offer-content {
        .additional-info,
        .balance-transfer {
            padding-bottom: 20px;

            p {
                color: #535353;
                font-family: Arial, sans-serif;
                margin-bottom: 0;
            }
        }
    }
}

.offer-image {
    min-height: 230px;
    max-height: 230px;
    width: 100%;
    margin-bottom: 2em;

    img {
        max-width: 100%;
        max-height: 100%;
    }
}

.offer-heading {
    font-size: 30px;
    font-weight: bold;
    color: #19398a;

    @media (max-width: 767px) {
        font-size: 25px;
    }
}

.offer-content {
    .offer-tagline {
        font-size: 24px;
        font-weight: normal;
        color: #19398a;

        @media (max-width: 767px) {
            font-size: 20px;
        }
    }

    .offer-ol {
        padding-left: 30px;
        padding-bottom: 10px;

        li,
        p {
            color: #535353;
            font-family: Arial, sans-serif;
            margin-bottom: 0;
        }
    }

    .decision {
        color: #535353;
        font-family: Arial, sans-serif;
    }
}

.submitButton {
    background: #333333;
    border: 1px solid #333333;
    box-shadow: none;
    width: 50%;
    text-align: center;

    &:hover,
    &[disabled]:hover,
    &:focus,
    &[disabled]:focus {
        background-color: #333333;
        border-color: #333333;
        color: #fff;
        background-position: 0;
        box-shadow: none;
        text-decoration: none;
    }

    @media (max-width: 1199px) {
        width: 60%;
        text-align: center;
    }
}

.optout-mobile {
    display: none;

    @media (max-width: $bp-medium) {
        display: block;
    }
}

.bottom-border-solid {
    border: 0;

    @media (max-width: $bp-medium) {
        padding-bottom: 2.5em;
        border-bottom: 1px solid black;
    }
}
</style>
